/**
 * ChatCommandContext
 *
 * Lightweight command channel for pages to request chat actions.
 * Allows any page to open the chat drawer with a pre-filled message.
 */

import {
  createContext,
  createEffect,
  on,
  useContext,
  createSignal,
  type Accessor,
  type ParentProps,
} from "solid-js";
import type { ChatViewContext, ProjectId } from "../api/types";

interface ChatCommand {
  message: string;
  /** Overrides the drawer's own view context for this message only (FLOW-802). */
  context?: ChatViewContext;
  /** The project this command was requested under, so a consumer whose project has since changed can drop it (FLOW-829). */
  projectId?: ProjectId;
}

interface ChatCommandContextValue {
  /** The pending command (if any) */
  pendingCommand: () => ChatCommand | null;
  /** Request that the chat drawer open with this message pre-filled */
  requestChat: (message: string, context?: ChatViewContext) => void;
  /** Clear the pending command (called by ChatDrawer after consuming) */
  clearCommand: () => void;
}

const ChatCommandContext = createContext<ChatCommandContextValue>();

export function ChatCommandProvider(
  props: ParentProps<{ projectId?: Accessor<ProjectId | undefined> }>,
) {
  const [pendingCommand, setPendingCommand] = createSignal<ChatCommand | null>(
    null,
  );

  // Discard any queued command when the active project changes
  if (props.projectId) {
    createEffect(
      on(props.projectId, () => setPendingCommand(null), { defer: true }),
    );
  }

  const requestChat = (message: string, context?: ChatViewContext) => {
    setPendingCommand({ message, context, projectId: props.projectId?.() });
  };

  const clearCommand = () => {
    setPendingCommand(null);
  };

  return (
    <ChatCommandContext.Provider
      value={{ pendingCommand, requestChat, clearCommand }}
    >
      {props.children}
    </ChatCommandContext.Provider>
  );
}

export function useChatCommand(): ChatCommandContextValue {
  const ctx = useContext(ChatCommandContext);
  if (!ctx)
    throw new Error("useChatCommand must be used within ChatCommandProvider");
  return ctx;
}
